import { removeHtmlTag } from '@exam-management/common/utils';
import {
  type QBankStructureLevel,
  QBankStructureLevelCategory,
} from '@exam-management/qbs-provider';
import { z } from 'zod';

import {
  ConvertEmptyStringToUndefinedSchema,
  NullishSearchSelectionSchema,
} from './general-schema';
import { getLocationSchema } from './qcontent-item-set-location-schema';

export function getQContentCreateSchema(qbsLevels: QBankStructureLevel[]) {
  return z.object({
    location: z.object({
      [QBankStructureLevelCategory.Part]: getLocationSchema(
        QBankStructureLevelCategory.Part,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Subject]: getLocationSchema(
        QBankStructureLevelCategory.Subject,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Group]: getLocationSchema(
        QBankStructureLevelCategory.Group,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Chapter]: getLocationSchema(
        QBankStructureLevelCategory.Chapter,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.Topic]: getLocationSchema(
        QBankStructureLevelCategory.Topic,
        qbsLevels,
      ),
      [QBankStructureLevelCategory.SubTopic]: getLocationSchema(
        QBankStructureLevelCategory.SubTopic,
        qbsLevels,
      ),
      LOQ: z
        .object({
          selected: z.enum(['true', 'false'], {
            //REMARK: this is workaround for this disabled field at first
            //but it should be enabled when subtopic is selected
            //so we need to validate this field only when it is enabled but hide error message
            //when it is disabled
            message: '',
          }),
          value: NullishSearchSelectionSchema,
        })
        .refine(
          (loqInputValues) => {
            if (loqInputValues.selected === 'true' && !loqInputValues.value) {
              return false;
            }

            return true;
          },
          {
            message: 'กรุณาเลือก LOQ',
            path: ['value'],
          },
        ),
    }),
    information: z.object({
      calculated: z.string().optional(),
      pStats: z
        .array(
          z.object({
            selected: z.boolean().nullable(),
            firstUsedAt: z.preprocess(
              (arg) => (typeof arg == 'string' || arg instanceof Date ? new Date(arg) : arg),
              z.date().nullable(),
            ),
            pValue: z.number(),
            sampleSize: z.number().int().nonnegative(),
            calculatedAt: z.preprocess(
              (arg) => (typeof arg == 'string' || arg instanceof Date ? new Date(arg) : arg),
              z.date(),
            ),
            updatedBy: z.string().uuid(),
            updatedUserFullName: z.string(),
          }),
        )
        .nullish(),
      pValue: z.object({
        selected: z.enum(['pInitial', 'pStat']),
        pInitial: z
          .union([
            z
              .number({
                message: '',
              })
              .min(0, {
                message: 'เกินค่าต่ำสุด (0)',
              })
              .max(1, {
                message: 'เกินค่าสูงสุด (1)',
              }),
            z.literal(''),
          ])
          .nullish()
          .transform((value) => {
            if (value === '') {
              return undefined;
            }
            return value;
          }),
        pStat: z.number({ message: 'กรุณาเลือกค่า P จากข้อมูลสถิติ' }).optional(),
      }),
      bIndex: z.preprocess(
        (value) => {
          if (value === '') {
            return null;
          }
          return value;
        },
        z
          .number({
            message: '',
          })
          .min(-1, {
            message: 'เกินค่าต่ำสุด (-1)',
          })
          .max(1, {
            message: 'เกินค่าสูงสุด (1)',
          })
          .nullish(),
      ),
    }),
    detail: z.object({
      details: z.string().refine(
        (value) => {
          const details = removeHtmlTag(value, {
            customTag: 'p',
          }).trim();
          return details.length >= 1;
        },
        {
          message: 'กรุณากรอกคำถาม',
        },
      ),
      choices: z
        .array(
          z.object({
            value: z.string(),
            label: z
              .string()
              .trim()
              .refine(
                (value) => {
                  const label = removeHtmlTag(value, {
                    customTag: 'p',
                  }).trim();
                  return label.length >= 1;
                },
                {
                  message: 'กรุณากรอกตัวเลือก',
                },
              ),
            order: z.number(),
          }),
        )
        .min(2, 'กรุณากรอกตัวเลือกอย่างน้อย 2 ข้อ'),
      correctAnswer: z.object({
        value: z
          .string({
            message: 'กรุณาเลือกคำตอบที่ถูกต้อง',
          })
          .min(1, 'กรุณาเลือกคำตอบที่ถูกต้อง'),
      }),
    }),
    properties: z.object({
      cognitiveDomain: z.array(NullishSearchSelectionSchema).optional(),
      learningObjectiveStatement: NullishSearchSelectionSchema,
      solution: z.string().optional(),
      reference: z.string().optional(),
    }),
    owners: z.object({
      owner: ConvertEmptyStringToUndefinedSchema,
      audit1: ConvertEmptyStringToUndefinedSchema,
      audit2: ConvertEmptyStringToUndefinedSchema,
      audit3: ConvertEmptyStringToUndefinedSchema,
    }),
  });
}
